import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { onePhaseProgram, twoPhaseProgram, cryptoOnePhase, cryptoTwoPhase, instantFunded, futuresPlans } from '../mockData';
import { Info } from 'lucide-react';

const GetFunded = () => {
  const [mainTab, setMainTab] = useState('fx-cfd');
  const [fxSubTab, setFxSubTab] = useState('one-phase');
  const [cryptoSubTab, setCryptoSubTab] = useState('one-phase');
  const [tradingPlatform, setTradingPlatform] = useState('dxtrade');
  const [futuresPlatform, setFuturesPlatform] = useState('dxtrade');

  useEffect(() => {
    // Check if we should navigate to instant funded
    const targetSubTab = sessionStorage.getItem('fxSubTab');
    if (targetSubTab === 'instant') {
      setFxSubTab('instant');
      sessionStorage.removeItem('fxSubTab');
    }
  }, []);

  const renderChallengeCards = (challenges, showInactivityPeriod = false, expandedPhaseLabels = false, showInactivityAfterDailyCap = false, expandProfitTargetLabel = false, showFuturesInactivity = false, platform = 'dxtrade') => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-10">
        {challenges.map((challenge) => {
          let challengeLink = challenge.link;
          
          if (platform === 'mtr' && challenge.linkMTR) {
            challengeLink = challenge.linkMTR;
          } else if (platform === 'ctrader' && challenge.linkCTRADER) {
            challengeLink = challenge.linkCTRADER;
          } else if (platform === 'rithmic' && challenge.linkRITHMIC) {
            challengeLink = challenge.linkRITHMIC;
          }
          
          return (
          <Card
            key={challenge.id}
            className={`border border-gray-100 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white rounded-2xl ${
              challenge.featured ? 'border-[#10dfb2]/30 shadow-lg shadow-[#10dfb2]/10' : 'shadow-md'
            }`}
          >
            <CardContent className="p-6">
              {challenge.featured && (
                <div className="bg-[#10dfb2] text-black text-xs font-bold py-2 px-4 rounded-full inline-block mb-4 shadow-md">
                  ⭐ MOST POPULAR
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-3xl font-bold text-[#10dfb2] mb-2">
                  {challenge.size}
                </h3>
                <p className="text-2xl font-bold text-gray-900">{challenge.cost}</p>
              </div>
              <div className="space-y-3 mb-6 text-sm bg-gray-50 rounded-2xl p-4">
                {challenge.profitTarget && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">{expandProfitTargetLabel ? 'Profit Target per phase:' : 'Profit Target:'}</span>
                    <span className="font-bold text-gray-900">{challenge.profitTarget}</span>
                  </div>
                )}
                {showFuturesInactivity && (
                  <div className="space-y-2 border-t border-gray-200 pt-3 mt-3">
                    <div className="text-gray-600 font-medium">Inactivity Period:</div>
                    <div className="pl-4 space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Phase 1-4:</span>
                        <span className="font-bold text-gray-900">14 days</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Live Funded:</span>
                        <span className="font-bold text-gray-900">7 days</span>
                      </div>
                    </div>
                  </div>
                )}
                {challenge.phase1Target && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">{expandedPhaseLabels ? 'Phase 1 Profit Target:' : 'Phase 1:'}</span>
                      <span className="font-bold text-gray-900">{challenge.phase1Target}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">{expandedPhaseLabels ? 'Phase 2 Profit Target:' : 'Phase 2:'}</span>
                      <span className="font-bold text-gray-900">{challenge.phase2Target}</span>
                    </div>
                  </>
                )}
                {challenge.maxDrawdown && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Max Drawdown:</span>
                    <span className="font-bold text-gray-900">{challenge.maxDrawdown}</span>
                  </div>
                )}
                {challenge.dailyLoss && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Daily Loss:</span>
                    <span className="font-bold text-gray-900">{challenge.dailyLoss}</span>
                  </div>
                )}
                {showInactivityPeriod && !showInactivityAfterDailyCap && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Inactivity Period:</span>
                    <span className="font-bold text-gray-900">30 days</span>
                  </div>
                )}
                {challenge.dailyCap && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Daily Cap:</span>
                    <span className="font-bold text-gray-900">{challenge.dailyCap}</span>
                  </div>
                )}
                {showInactivityAfterDailyCap && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Inactivity Period:</span>
                    <span className="font-bold text-gray-900">30 days</span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t border-gray-200 pt-3 mt-3">
                  <span className="text-gray-600 font-medium">Profit Split:</span>
                  <span className="font-bold text-[#10dfb2] text-lg">{challenge.profitSplit}</span>
                </div>
              </div>
              <a href={challengeLink} target="_blank" rel="noopener noreferrer">
                <Button
                  className={`w-full transition-all duration-300 text-sm font-bold py-2.5 px-4 rounded-lg ${
                    challenge.featured
                      ? 'bg-gradient-to-r from-[#10dfb2] to-[#0bc99f] hover:shadow-lg hover:shadow-[#10dfb2]/50 text-black hover:-translate-y-1'
                      : 'bg-gradient-to-r from-gray-900 to-black hover:shadow-lg hover:shadow-gray-900/50 text-white hover:-translate-y-1'
                  }`}
                >
                  Get Started
                </Button>
              </a>
            </CardContent>
          </Card>
          );
        })}
      </div>
    );
  };

  // FX/CFD Instant Funded (filtered to match FX pricing structure)
  const fxInstant = instantFunded;

  return (
    <div className="min-h-screen pt-24 bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-black via-[#1A1A1A] to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-6xl md:text-7xl font-extrabold mb-6">
            Get <span className="text-[#10dfb2]">Funded</span> Today
          </h1>
          <p className="text-2xl md:text-3xl max-w-4xl mx-auto font-light">
            Choose your trading program and start your funded trading journey
          </p>
        </div>
      </section>

      {/* Main Programs Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="fx-cfd" className="w-full" onValueChange={setMainTab}>
            {/* Main Program Type Tabs - Larger & More Prominent */}
            <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-3 mb-12 h-16 p-2">
              <TabsTrigger value="fx-cfd" className="text-lg font-bold">FX / CFD</TabsTrigger>
              <TabsTrigger value="crypto" className="text-lg font-bold">Crypto</TabsTrigger>
              <TabsTrigger value="futures" className="text-lg font-bold">Futures</TabsTrigger>
            </TabsList>

            {/* FX/CFD CONTENT */}
            <TabsContent value="fx-cfd">
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  FX/CFD Trading Programs
                </h2>

                {/* Trading Platform Toggle - Clean Minimal Style */}
                <div className="flex justify-center mb-8">
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="platform"
                        value="dxtrade"
                        checked={tradingPlatform === 'dxtrade'}
                        onChange={() => setTradingPlatform('dxtrade')}
                        className="w-4 h-4 accent-[#10dfb2]"
                      />
                      <span className={`font-semibold text-lg transition-colors ${
                        tradingPlatform === 'dxtrade' ? 'text-[#10dfb2]' : 'text-gray-700'
                      }`}>
                        DXTRADE
                      </span>
                    </label>
                    
                    <div className="text-gray-400">|</div>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="platform"
                        value="mtr"
                        checked={tradingPlatform === 'mtr'}
                        onChange={() => setTradingPlatform('mtr')}
                        className="w-4 h-4 accent-[#10dfb2]"
                      />
                      <span className={`font-semibold text-lg transition-colors ${
                        tradingPlatform === 'mtr' ? 'text-[#10dfb2]' : 'text-gray-700'
                      }`}>
                        MTR
                      </span>
                    </label>
                    
                    <div className="text-gray-400">|</div>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="platform"
                        value="ctrader"
                        checked={tradingPlatform === 'ctrader'}
                        onChange={() => setTradingPlatform('ctrader')}
                        className="w-4 h-4 accent-[#10dfb2]"
                      />
                      <span className={`font-semibold text-lg transition-colors ${
                        tradingPlatform === 'ctrader' ? 'text-[#10dfb2]' : 'text-gray-700'
                      }`}>
                        CTRADER
                      </span>
                    </label>
                  </div>
                </div>
                
                {/* FX Sub-tabs */}
                <Tabs value={fxSubTab} onValueChange={setFxSubTab}>
                  <TabsList className="grid w-full max-w-xl mx-auto grid-cols-3 mb-6">
                    <TabsTrigger value="one-phase">One Phase</TabsTrigger>
                    <TabsTrigger value="two-phase">Two Phase</TabsTrigger>
                    <TabsTrigger value="instant">Instant Funded</TabsTrigger>
                  </TabsList>

                  {/* One Phase FX */}
                  <TabsContent value="one-phase">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">One Phase Challenge</h3>
                      <p className="text-gray-600 mb-4">Single 10% profit target evaluation</p>
                      <div className="inline-flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-3xl">
                        <Info className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                        <div className="text-sm text-gray-700">
                          <strong>Rules:</strong> 10% target • 5% daily loss • 6% max drawdown • 1:50 leverage • 80% split (No add-ons available)
                        </div>
                      </div>
                    </div>
                    {renderChallengeCards(onePhaseProgram, true, false, false, false, false, tradingPlatform)}
                  </TabsContent>

                  {/* Two Phase FX */}
                  <TabsContent value="two-phase">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Two Phase Challenge</h3>
                      <p className="text-gray-600 mb-4">Lower cost entry with 8% + 5% targets</p>
                      <div className="inline-flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-3xl">
                        <Info className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                        <div className="text-sm text-gray-700">
                          <strong>Rules:</strong> Phase 1: 8% • Phase 2: 5% • 5% daily loss • 8% max drawdown • 1:50 leverage • 80% split (No add-ons available)
                        </div>
                      </div>
                    </div>
                    {renderChallengeCards(twoPhaseProgram, true, true, false, false, false, tradingPlatform)}
                  </TabsContent>

                  {/* Instant FX */}
                  <TabsContent value="instant" data-section="instant-funded">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Instant Funded</h3>
                      <p className="text-gray-600 mb-4">No evaluation - Start trading immediately</p>
                      <div className="inline-flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-3xl">
                        <Info className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                        <div className="text-sm text-gray-700">
                          <strong>Rules:</strong> 5% daily loss • 8% trailing drawdown • 1:50 leverage • 80% split • Add-ons: Weekend Hold (+10%), 90% Split (+20%)
                        </div>
                      </div>
                    </div>
                    {renderChallengeCards(fxInstant, true, false, false, false, false, tradingPlatform)}
                  </TabsContent>
                </Tabs>
              </div>
            </TabsContent>

            {/* CRYPTO CONTENT */}
            <TabsContent value="crypto">
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Crypto Trading Programs
                </h2>
                
                {/* Crypto Sub-tabs */}
                <Tabs value={cryptoSubTab} onValueChange={setCryptoSubTab}>
                  <TabsList className="grid w-full max-w-xl mx-auto grid-cols-2 mb-6">
                    <TabsTrigger value="one-phase">One Phase</TabsTrigger>
                    <TabsTrigger value="two-phase">Two Phase</TabsTrigger>
                  </TabsList>

                  {/* Crypto One Phase */}
                  <TabsContent value="one-phase">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Crypto One Phase</h3>
                      <p className="text-gray-600 mb-4">9% profit target with 90% split</p>
                      <div className="inline-flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-3xl">
                        <Info className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                        <div className="text-sm text-gray-700">
                          <strong>Rules:</strong> 9% target • 6% max drawdown • ±3% daily cap • 5:1 leverage (BTC/ETH), 2:1 (others) • 90% split
                        </div>
                      </div>
                    </div>
                    {renderChallengeCards(cryptoOnePhase, false, false, true)}
                  </TabsContent>

                  {/* Crypto Two Phase */}
                  <TabsContent value="two-phase">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Crypto Two Phase</h3>
                      <p className="text-gray-600 mb-4">Lower cost with 6% + 9% targets</p>
                      <div className="inline-flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-3xl">
                        <Info className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                        <div className="text-sm text-gray-700">
                          <strong>Rules:</strong> Phase 1: 6% • Phase 2: 9% • 9% max drawdown • ±3% daily cap • 5:1 leverage (BTC/ETH) • 90% split
                        </div>
                      </div>
                    </div>
                    {renderChallengeCards(cryptoTwoPhase, false, true, true)}
                  </TabsContent>
                </Tabs>

                <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> Crypto programs do not have instant funded options. No add-ons available.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* FUTURES CONTENT */}
            <TabsContent value="futures">
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Futures Trading Program
                </h2>
                
                {/* Futures Platform Toggle - Clean Minimal Style */}
                <div className="flex justify-center mb-8">
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="futures-platform"
                        value="dxtrade"
                        checked={futuresPlatform === 'dxtrade'}
                        onChange={() => setFuturesPlatform('dxtrade')}
                        className="w-4 h-4 accent-[#10dfb2]"
                      />
                      <span className={`font-semibold text-lg transition-colors ${
                        futuresPlatform === 'dxtrade' ? 'text-[#10dfb2]' : 'text-gray-700'
                      }`}>
                        DXTRADE
                      </span>
                    </label>
                    
                    <div className="text-gray-400">|</div>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="futures-platform"
                        value="rithmic"
                        checked={futuresPlatform === 'rithmic'}
                        onChange={() => setFuturesPlatform('rithmic')}
                        className="w-4 h-4 accent-[#10dfb2]"
                      />
                      <span className={`font-semibold text-lg transition-colors ${
                        futuresPlatform === 'rithmic' ? 'text-[#10dfb2]' : 'text-gray-700'
                      }`}>
                        RITHMIC
                      </span>
                    </label>
                  </div>
                </div>
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Multi-Phase Futures</h3>
                  <p className="text-gray-600 mb-4">9% profit per phase with phased payouts</p>
                  <div className="inline-flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-3xl">
                    <Info className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                    <div className="text-sm text-gray-700">
                      <strong>Rules:</strong> 9% per phase • 5% max loss (trails EOD) • 25% consistency rule • 60 days/phase • 90% split
                    </div>
                  </div>
                </div>
                
                {futuresPlatform === 'dxtrade' ? (
                  renderChallengeCards(futuresPlans.filter(plan => !plan.id.includes('rithmic')), false, false, false, true, true, futuresPlatform)
                ) : (
                  renderChallengeCards(futuresPlans.filter(plan => plan.id.includes('rithmic')), false, false, false, true, true, futuresPlatform)
                )}

                <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-5 max-w-3xl mx-auto">
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                    <Info className="text-yellow-600 mr-2" size={18} />
                    Consistency Rule
                  </h4>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Formula:</strong> (Best Day PnL ÷ Total PnL) × 100 ≤ 25%
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Example:</strong> $8K target, best day $2K → 25% ✓ | Best day $3K → 37.5% ✗
                  </p>
                </div>

                <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> Futures programs do not have instant funded options.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#10dfb2] to-[#10dfb2] text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Start Trading?
          </h2>
          <p className="text-lg mb-8">
            Select your program above and begin your funded trading journey
          </p>
          <Link to="#get-funded" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <Button
              size="lg"
              className="bg-black text-[#10dfb2] hover:bg-gray-900 font-semibold text-lg px-12 py-6 transition-all duration-300 hover:scale-105"
            >
              View Programs
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GetFunded;
